import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  hasPendingPostLoginRedirect,
  usePostLoginRedirect,
} from './usePostLoginRedirect'
import * as Sentry from '@sentry/nextjs'
import { useRouter } from 'next/router'

import Keycloak from 'keycloak-js'

import { useMeQuery, useMySettingQuery } from '@/graphql/generated/hooks'
import { useAppDispatch } from '@/redux/hooks'
import { setMe } from '@/redux/me/meSlice'
import {
  resetNotifications,
  setNotifications,
} from '@/redux/notifications/notificationsSlice'
import { store } from '@/redux/store'
import { selectTokens } from '@/redux/token/tokenSlice'
import {
  resetUi,
  setIncidentAudioAlert,
  setUserSettingsLoaded,
} from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import {
  fetchMemoryOnlyTokenStorageFlag,
  getCachedMemoryOnlyTokenStorageFlag,
} from './runtimeMemoryTokenFlag'
import {
  clearTokensForCurrentMode,
  getAccessTokenForCurrentMode,
  getIdTokenForCurrentMode,
  getTokenStorageMode,
  setTokenStorageMode,
  writeTokensForCurrentMode,
} from './tokenStorageBridge'
import { AuthClientEvent, AuthClientTokens, ROLE_STATUS } from './types'
import {
  MEMORY_TOKEN_FLAG_SYNC_INTERVAL_MS,
  MIN_TOKEN_VALIDITY_IN_MINUTES,
  getInitOptions,
  getKeycloakInstance,
  getRoleStatus,
  isValidToken,
} from './utils'

interface KeyCloakProviderIProps {
  autoRefreshToken?: boolean
  children: ReactNode
}

type KeyCloakProviderContextT = {
  keycloak: Keycloak
  logIn: () => void
  logOut: () => void
  isKcInitilised: boolean
  isValidLogin: boolean
  isResolvingRedirect: boolean
  roleStatus: ROLE_STATUS
}

export const KeyCloakContext = createContext<KeyCloakProviderContextT>(null)

export const KeyCloakProvider = ({
  autoRefreshToken,
  children,
}: KeyCloakProviderIProps) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [keycloak] = useState<Keycloak>(getKeycloakInstance())
  const { clear, restoreIfNeeded, saveCurrentLocation } = usePostLoginRedirect()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isKcInitilised, setIsKcInitilised] = useState(false)
  // Gate the children render only when there's a pending post-login redirect to
  // avoid the brief flash of "/" before router.replace navigates to the saved
  // path. In normal cold loads this stays false from the first render.
  const [isResolvingRedirect, setIsResolvingRedirect] = useState(() => {
    return hasPendingPostLoginRedirect()
  })
  const roleStatus = getRoleStatus(accessToken)
  const isValidLogin = useMemo(
    () =>
      roleStatus !== ROLE_STATUS.NOT_SET &&
      isValidToken(accessToken) &&
      isKcInitilised,
    [accessToken, isKcInitilised, roleStatus]
  )
  const {
    data,
    loading: isLoading,
    client,
  } = useMeQuery({
    fetchPolicy: 'network-only',
    skip: !isValidLogin,
  })
  const user = data?.me
  const { data: mySettingData, loading: isMySettingLoading } =
    useMySettingQuery({
      fetchPolicy: 'no-cache',
      skip: !isValidLogin || !user?.id,
    })

  const initializeKeyCloak = () => {
    // Attach Keycloak listeners
    keycloak.onReady = updateState('onReady')
    keycloak.onAuthSuccess = updateState('onAuthSuccess')
    keycloak.onAuthError = updateState('onAuthError')
    keycloak.onAuthRefreshSuccess = updateState('onAuthRefreshSuccess')
    keycloak.onAuthRefreshError = updateState('onAuthRefreshError')
    keycloak.onAuthLogout = updateState('onAuthLogout')
    keycloak.onTokenExpired = refreshToken('onTokenExpired')

    const isLegacyMode = getTokenStorageMode() === 'legacy'
    const legacyTokens = isLegacyMode ? selectTokens(store.getState()) : {}

    // FF OFF (legacy): bootstrap Keycloak with persisted Redux tokens.
    // FF ON (memory-only): initialize without persisted tokens.
    keycloak
      .init({ ...legacyTokens, ...getInitOptions() })
      .catch(updateState('onInitError'))
  }

  const syncTokenStorageMode = useCallback(async (force = false) => {
    const token = getAccessTokenForCurrentMode()
    const isMemoryOnlyEnabled = await fetchMemoryOnlyTokenStorageFlag(
      token,
      force
    )
    setTokenStorageMode(isMemoryOnlyEnabled ? 'memory-only' : 'legacy')
    setAccessToken(getAccessTokenForCurrentMode())
  }, [])

  const updateState = (event: AuthClientEvent) => () => {
    onEvent(event)
    const { idToken, refreshToken, token } = keycloak
    onTokens({
      idToken,
      refreshToken,
      token,
    })
  }

  const refreshToken = (event: AuthClientEvent) => () => {
    onEvent(event)
    if (autoRefreshToken !== false) {
      // Refresh Keycloak token
      keycloak.updateToken(MIN_TOKEN_VALIDITY_IN_MINUTES)
    }
  }

  const onEvent = async (event: AuthClientEvent) => {
    switch (event) {
      case 'onAuthError': {
        keycloak.clearToken()
        break
      }
      case 'onAuthLogout': {
        resetReduxState()
        client.resetStore()
        mixpanel.track('Logout')
        Sentry.setUser(null)
        break
      }
      case 'onAuthRefreshError': {
        handleInvalidSession()
        break
      }
      case 'onAuthRefreshSuccess': {
        break
      }
      case 'onAuthSuccess': {
        mixpanel.track('Login', {
          type: 'KC',
          realm: keycloak.realm,
        })
        break
      }
      case 'onInitError': {
        break
      }
      case 'onReady': {
        handleOnReady()
        break
      }
      case 'onTokenExpired': {
        break
      }
      default:
        break
    }
  }

  const onTokens = useCallback((tokens: AuthClientTokens) => {
    if (!tokens.token) {
      return
    }

    writeTokensForCurrentMode(tokens)
    setAccessToken(getAccessTokenForCurrentMode())
  }, [])

  const logIn = async () => {
    try {
      saveCurrentLocation()
      window.localStorage.removeItem('viewport')
      await keycloak.login({ redirectUri: window.location.origin })
    } catch (error) {
      // TODO Handle error
    }
  }

  const logOut = async () => {
    try {
      clear()
      await keycloak.logout({ redirectUri: window.location.origin })
    } catch (error) {
      // TODO Handle error
    }
  }

  const handleOnReady = async () => {
    try {
      // Check if a validation token, if not update
      if (keycloak.isTokenExpired()) {
        await keycloak.updateToken(-1)
      }

      setIsKcInitilised(true)
      setAccessToken(getAccessTokenForCurrentMode())
      const didRedirect = restoreIfNeeded()
      if (!didRedirect) setIsResolvingRedirect(false)
    } catch {
      // No token, redirect to login
      setIsResolvingRedirect(false)
      logIn()
    }
  }

  const handleInvalidSession = async () => {
    keycloak.idToken = getIdTokenForCurrentMode()
    keycloak.logout()
  }

  const resetReduxState = () => {
    dispatch(resetUi())
    dispatch(setMe(false))
    dispatch(setNotifications([]))
    dispatch(resetNotifications())
    clearTokensForCurrentMode()
    setAccessToken(getAccessTokenForCurrentMode())
  }

  useEffect(() => {
    // Initialize Keycloak once on mount to register auth listeners and start
    // the login/session lifecycle for the app.
    initializeKeyCloak()
  }, [])

  useEffect(() => {
    // Keep startup behavior deterministic when cache is already warm.
    setTokenStorageMode(
      getCachedMemoryOnlyTokenStorageFlag() ? 'memory-only' : 'legacy'
    )
  }, [])

  useEffect(() => {
    if (!isValidLogin) return

    // This runtime flag changes rarely; consider mount-only fetches to avoid
    // periodic backend calls.
    syncTokenStorageMode(true)
    const intervalId = window.setInterval(() => {
      syncTokenStorageMode()
    }, MEMORY_TOKEN_FLAG_SYNC_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [isValidLogin, syncTokenStorageMode])

  useEffect(() => {
    if (!isResolvingRedirect) return
    const off = () => setIsResolvingRedirect(false)
    router.events.on('routeChangeComplete', off)
    router.events.on('routeChangeError', off)
    return () => {
      router.events.off('routeChangeComplete', off)
      router.events.off('routeChangeError', off)
    }
  }, [router, isResolvingRedirect])

  useEffect(() => {
    if (user && isValidLogin) {
      mixpanel.track('Login', {
        realm: keycloak.realm,
        type: 'GQL',
        firstName: user?.firstName,
        lastName: user?.lastName,
        role: user?.roleName,
        userName: user?.email,
        email: user?.email,
        userId: user?.id,
      })
      // Set Sentry user context so errors are associated with the logged-in user
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.username,
        role: user?.roleName,
      })
      dispatch(setMe(user))
    }
  }, [isValidLogin, isLoading])

  useEffect(() => {
    if (isValidLogin && user?.id) {
      dispatch(setUserSettingsLoaded(false))
    }
  }, [dispatch, isValidLogin, user?.id])

  useEffect(() => {
    const isAudibleAlertsEnabled = mySettingData?.mySetting.audibleAlertsEnabled

    // We fetch with no-cache and wait for a valid logged-in user id to avoid
    // syncing stale values across logout/login transitions.
    if (!isValidLogin || !user?.id || isMySettingLoading) {
      return
    }

    if (isAudibleAlertsEnabled !== undefined) {
      dispatch(setIncidentAudioAlert(isAudibleAlertsEnabled))
    }

    dispatch(setUserSettingsLoaded(true))
  }, [
    dispatch,
    isMySettingLoading,
    isValidLogin,
    user?.id,
    mySettingData?.mySetting?.audibleAlertsEnabled,
  ])

  return (
    <KeyCloakContext.Provider
      value={{
        keycloak,
        isKcInitilised,
        isValidLogin,
        isResolvingRedirect,
        roleStatus,
        logIn,
        logOut,
      }}
    >
      {children}
    </KeyCloakContext.Provider>
  )
}

export const useKeyCloak = () => {
  return useContext(KeyCloakContext)
}
